import React, { useContext, useEffect, useState } from "react";

import { InvestmentDTO } from "../../model/investmentDTO";
import { TabContainer, ChartData } from "../TabContainer/TabContainer";
import {
  SeriesDTO,
  ChartOptionsForTierParticpation,
  TierParticpationParams,
} from "../../model/ChartOptionsForTierParticpation";
import {
  ChartOptionsForCapRate,
  CapRateParams,
} from "../../model/ChartOptionsForCapRate";
import {
  ChartOptionsForStepRate,
  StepRateParams,
} from "../../model/ChartOptionsForStepRate";
import {
  ChartOptionsForDualDirection,
  DualDirectionRateParams,
} from "../../model/ChartOptionsForDualDirection";
import {
  UserInterfaceHints,
  UserInterfaceHintsContext,
} from "../Store/UserInterfaceHintsContext";

interface ResultsContainerProps {
  currentStrategyData: InvestmentDTO;
  view: string;
}

export const ResultsContainer = (props: ResultsContainerProps) => {
  const prepareStrategicReturnSeriesData = (strategyType: string) => {
    const dataobj = props.currentStrategyData;
    if (dataobj) {
      if (strategyType === "tieredParticipation" || strategyType === "step") {
        const st1 =
          dataobj.valuesDownMarket.strategyReturnPercentage -
          dataobj.valuesDownMarket.additionalReturnPercentage;
        const st2 =
          dataobj.valuesUpMarket.strategyReturnPercentage -
          dataobj.valuesUpMarket.additionalReturnPercentage;
        const st3 =
          dataobj.valuesCustomMarket.strategyReturnPercentage -
          dataobj.valuesCustomMarket.additionalReturnPercentage;

        return [st1, st2, st3, null];
      } else {
          if(strategyType === 'dualDirection'){
            const st1 = dataobj.valuesDownMarketLessThanEqualBuffer.strategyReturnPercentage;
            const st2 = dataobj.valuesDownMarketGreaterThanBuffer.strategyReturnPercentage;
            const st3 = dataobj.valuesUpMarket.strategyReturnPercentage;
            const st4 = dataobj.valuesCustomMarket.strategyReturnPercentage;

            return [st1, st2, st3, st4, null];
          } else {
            const st1 = dataobj.valuesDownMarket.strategyReturnPercentage;
            const st2 = dataobj.valuesUpMarket.strategyReturnPercentage;
            const st3 = dataobj.valuesCustomMarket.strategyReturnPercentage;

            return [st1, st2, st3, null];
          }
      }
    }

    return [];
  };
  const prepareIndexReturnPercentage = () => {
    const dataobj = props.currentStrategyData;
    const strategyType = dataobj.calculatorInputsDTO.strategyType;
    
    if (dataobj) {
      if(strategyType === 'dualDirection'){
        const st1 = dataobj.valuesDownMarketLessThanEqualBuffer.indexReturnPercentage;
        const st2 = dataobj.valuesDownMarketGreaterThanBuffer.indexReturnPercentage;
        const st3 = dataobj.valuesUpMarket.indexReturnPercentage;
        const st4 = dataobj.valuesCustomMarket.indexReturnPercentage;

        return [st1, st2, st3, st4, null];
      } else {
        const st1 = dataobj.valuesDownMarket.indexReturnPercentage;
        const st2 = dataobj.valuesUpMarket.indexReturnPercentage;
        const st3 = dataobj.valuesCustomMarket.indexReturnPercentage;

        return [st1, st2, st3, null];
      }
    }
    return [];
  };
  const prepareAdditionalGain = () => {
    const dataobj = props.currentStrategyData;
    const strategyType = dataobj.calculatorInputsDTO.strategyType;
    if (dataobj) {
      if(strategyType === 'dualDirection'){
        const st1 = dataobj.valuesDownMarketLessThanEqualBuffer.additionalReturnPercentage > 0
                      ? dataobj.valuesDownMarketLessThanEqualBuffer.additionalReturnPercentage
                      : null;
        const st2 = dataobj.valuesDownMarketGreaterThanBuffer.additionalReturnPercentage > 0
                      ? dataobj.valuesDownMarketGreaterThanBuffer.additionalReturnPercentage
                      : null;
        const st3 = dataobj.valuesUpMarket.additionalReturnPercentage > 0
                      ? dataobj.valuesUpMarket.additionalReturnPercentage
                      : null;
        const st4 = dataobj.valuesCustomMarket.additionalReturnPercentage > 0
                      ? dataobj.valuesCustomMarket.additionalReturnPercentage
                      : null;

        return [st1, st2, st3, st4, null];
      } else {
        const st1 =
          dataobj.valuesDownMarket.additionalReturnPercentage > 0
            ? dataobj.valuesDownMarket.additionalReturnPercentage
            : null;
        const st2 =
          dataobj.valuesUpMarket.additionalReturnPercentage > 0
            ? dataobj.valuesUpMarket.additionalReturnPercentage
            : null;
        const st3 =
          dataobj.valuesCustomMarket.additionalReturnPercentage > 0
            ? dataobj.valuesCustomMarket.additionalReturnPercentage
            : null;

        return [st1, st2, st3, null];
      }
    }
    return [];
  };

  const getMinMaxDataPoints = (dataobj: InvestmentDTO) => {
    const datapoints = [];
    const isStrategy1: boolean =
      dataobj.calculatorInputsDTO.strategyType === "tieredParticipation";
    const isDualDirectionStrategy: boolean = dataobj.calculatorInputsDTO.strategyType === 'dualDirection';

    if(isDualDirectionStrategy){
      datapoints.push(dataobj.valuesDownMarketLessThanEqualBuffer.indexReturnPercentage);
      datapoints.push(dataobj.valuesDownMarketGreaterThanBuffer.indexReturnPercentage);
      datapoints.push(dataobj.valuesDownMarketLessThanEqualBuffer.strategyReturnPercentage);
      datapoints.push(dataobj.valuesDownMarketGreaterThanBuffer.strategyReturnPercentage);
    } else {
      datapoints.push(dataobj.valuesDownMarket.indexReturnPercentage);
      datapoints.push(
        isStrategy1
          ? dataobj.valuesDownMarket.strategyReturnPercentage +
              dataobj.valuesDownMarket.additionalReturnPercentage
          : dataobj.valuesDownMarket.strategyReturnPercentage
      );
    }
    datapoints.push(dataobj.valuesUpMarket.indexReturnPercentage);
    datapoints.push(
      isStrategy1
        ? dataobj.valuesUpMarket.strategyReturnPercentage +
            dataobj.valuesUpMarket.additionalReturnPercentage
        : dataobj.valuesUpMarket.strategyReturnPercentage
    );
    datapoints.push(dataobj.valuesCustomMarket.indexReturnPercentage);
    datapoints.push(
      isStrategy1
        ? dataobj.valuesCustomMarket.strategyReturnPercentage +
            dataobj.valuesCustomMarket.additionalReturnPercentage
        : dataobj.valuesCustomMarket.strategyReturnPercentage
    );
    const st5 = Math.min(...datapoints);
    const st6 = Math.max(...datapoints);
    return [st5, st6];
  };

  const getTierParams = (): TierParticpationParams => {
    const dataobj = props.currentStrategyData;
    if (dataobj) {
      const st1 = dataobj.valuesCustomMarket.tierLevel;
      const st2 = dataobj.valuesCustomMarket.tier1ParticipationRate;
      const st3 = dataobj.valuesCustomMarket.tier2ParticipationRate;
      const st4 = dataobj.calculatorInputsDTO.bufferLevel * -1;
      const st7 = dataobj.valuesCustomMarket.strategyReturnPercentage;
      const st8 = dataobj.valuesUpMarket.strategyReturnPercentage;
      const [st5, st6] = getMinMaxDataPoints(dataobj);
      const tierObj = {
        tierLevel: st1,
        tier1ParticipationRate: st2,
        tier2ParticipationRate: st3,
        bufferLevel: st4,
        minDataPoint: st5,
        maxDataPoint: st6,
        strategyReturnPercentageCustomMarket: st7,
        strategyReturnPercentageUpMarket: st8,
      };
      return tierObj;
    }

    return {} as TierParticpationParams;
  };
  const getCapParams = (): CapRateParams => {
    const dataobj = props.currentStrategyData;
    if (dataobj) {
      const st1 = dataobj.valuesCustomMarket.capRate;
      const st2 = dataobj.calculatorInputsDTO.bufferLevel * -1;
      const st4 = dataobj.valuesCustomMarket.indexReturnPercentage;
      const [st5, st6] = getMinMaxDataPoints(dataobj);
      const st7 = dataobj.valuesCustomMarket.strategyReturnPercentage;
      const st8 = dataobj.valuesUpMarket.strategyReturnPercentage;
      const st9 = dataobj.valuesUpMarket.indexReturnPercentage;
      const capObj = {
        capRate: st1,
        bufferLevel: st2,
        indexReturnPercentageCustom: st4,
        minDataPoint: st5,
        maxDataPoint: st6,
        strategyReturnPercentageCustomMarket: st7,
        strategyReturnPercentageUpMarket: st8,
        indexReturnPercentageUp: st9,
      };
      return capObj;
    }
    return {} as CapRateParams;
  };
  const getStepParams = (): StepRateParams => {
    const dataobj = props.currentStrategyData;
    if (dataobj) {
      const st1 = dataobj.valuesCustomMarket.stepRate;
      const st2 = dataobj.valuesCustomMarket.stepParticipationRate;
      const act = dataobj.valuesCustomMarket.actualStepParRate;
      const st3 = dataobj.calculatorInputsDTO.bufferLevel * -1;
      const st4 = dataobj.valuesCustomMarket.indexReturnPercentage;
      const [st5, st6] = getMinMaxDataPoints(dataobj);
      const st7 = dataobj.valuesCustomMarket.strategyReturnPercentage;
      const st8 = dataobj.valuesUpMarket.indexReturnPercentage;
      const st9 = dataobj.valuesUpMarket.strategyReturnPercentage;

      const stepObj = {
        stepRate: st1,
        stepParticipationRate: st2,
        actualStepParRate: act,
        bufferLevel: st3,
        indexReturnPercentageCustom: st4,
        minDataPoint: st5,
        maxDataPoint: st6,
        strategyReturnPercentageCustom: st7,
        indexReturnPercentageUp: st8,
        strategyReturnPercentageUp: st9,
      };
      return stepObj;
    }
    return {} as StepRateParams;
  };

  const getDualDirectionRateParams = (): DualDirectionRateParams => {
    const dataobj = props.currentStrategyData;
    if (dataobj) {
      const st1 = dataobj.valuesCustomMarket.capRate;
      const st2 = dataobj.calculatorInputsDTO.bufferLevel * -1;
      const st4 = dataobj.valuesCustomMarket.indexReturnPercentage;
      const [st5, st6] = getMinMaxDataPoints(dataobj);
      const st7 = dataobj.valuesCustomMarket.strategyReturnPercentage;
      const st8 = dataobj.valuesUpMarket.strategyReturnPercentage;
      const st9 = dataobj.valuesUpMarket.indexReturnPercentage;
      const st10 = dataobj.valuesDownMarketLessThanEqualBuffer.strategyReturnPercentage;
      const st11 = dataobj.valuesDownMarketGreaterThanBuffer.strategyReturnPercentage;
      const capObj = {
        capRate: st1,
        bufferLevel: st2,
        indexReturnPercentageCustom: st4,
        minDataPoint: st5,
        maxDataPoint: st6,
        strategyReturnPercentageCustomMarket: st7,
        strategyReturnPercentageUpMarket: st8,
        indexReturnPercentageUp: st9,
        strategyReturnPercentageDownMarketLessThanEqualBuffer: st10,
        strategyReturnPercentageDownMarketGreaterThanBuffer: st11,
      };
      return capObj;
    }
    return {} as DualDirectionRateParams;
  };

  const [chartOptions, setChartOptions] = useState<ChartData>(undefined);
  const hints = useContext(UserInterfaceHintsContext);

  useEffect(() => {
    if (props.currentStrategyData) {
      const indexReturnSeriesDTO = new SeriesDTO(
        prepareIndexReturnPercentage(),
        "#A0C7DF",
        "Index Return",
        0,
        undefined
      );
      const strategyReturnSeriesDTO = new SeriesDTO(
        prepareStrategicReturnSeriesData(
          props.currentStrategyData.calculatorInputsDTO.strategyType
        ),
        "#183455",
        "Strategy Return",
        1,
        "stack"
      );
      const additionalGainArr = prepareAdditionalGain();
      const filteredAdditionalGainArr = additionalGainArr.filter(function (x) {
        if (x) return x;
        else return false;
      });
      const asterisk = "<sup>*</sup>";
      const additionalGainSeriesDTO = new SeriesDTO(
        additionalGainArr,
        "url(#custom-pattern)",
        "Additional Gain" + asterisk,
        2,
        "stack"
      );

      if (filteredAdditionalGainArr && filteredAdditionalGainArr[0]) {
        hints.addHint(UserInterfaceHints.ADDITIONAL_GAIN_SHOWN);
      } else {
        hints.removeHint(UserInterfaceHints.ADDITIONAL_GAIN_SHOWN);
      }

      if (
        props.currentStrategyData.calculatorInputsDTO.strategyType ===
        "tieredParticipation"
      ) {
        if (filteredAdditionalGainArr && filteredAdditionalGainArr[0]) {
          setChartOptions(
            new ChartOptionsForTierParticpation(
              [
                indexReturnSeriesDTO,
                additionalGainSeriesDTO,
                strategyReturnSeriesDTO,
              ],
              getTierParams(),
              null
            )
          );
        } else {
          setChartOptions(
            new ChartOptionsForTierParticpation(
              [indexReturnSeriesDTO, strategyReturnSeriesDTO],
              getTierParams(),
              null
            )
          );
        }
      } else if (
        props.currentStrategyData.calculatorInputsDTO.strategyType ===
        "pointToPointCap"
      ) {
        setChartOptions(
          new ChartOptionsForCapRate(
            [indexReturnSeriesDTO, strategyReturnSeriesDTO],
            getCapParams(),
            null
          )
        );
      } else if (
        props.currentStrategyData.calculatorInputsDTO.strategyType === "step"
      ) {
        if (filteredAdditionalGainArr && filteredAdditionalGainArr[0]) {
          setChartOptions(
            new ChartOptionsForStepRate(
              [
                indexReturnSeriesDTO,
                additionalGainSeriesDTO,
                strategyReturnSeriesDTO,
              ],
              getStepParams(),
              null
            )
          );
        } else {
          setChartOptions(
            new ChartOptionsForStepRate(
              [indexReturnSeriesDTO, strategyReturnSeriesDTO],
              getStepParams(),
              null
            )
          );
        }
      } else if (
        props.currentStrategyData.calculatorInputsDTO.strategyType === "dualDirection"
      ) {
        if (filteredAdditionalGainArr && filteredAdditionalGainArr[0]) {
          setChartOptions(
            new ChartOptionsForDualDirection(
              [
                indexReturnSeriesDTO,
                additionalGainSeriesDTO,
                strategyReturnSeriesDTO,
              ],
              getDualDirectionRateParams(),
              null
            )
          );
        } else {
          setChartOptions(
            new ChartOptionsForDualDirection(
              [indexReturnSeriesDTO, strategyReturnSeriesDTO],
              getDualDirectionRateParams(),
              null
            )
          );
        }
      }
    }
  }, [props.currentStrategyData]);

  return (
    <>
      {chartOptions ? (
        <TabContainer
          chartOptions={chartOptions}
          strategyData={props.currentStrategyData}
          view={props.view}
        />
      ) : (
        ""
      )}
    </>
  );
};
